<template>
    <footer
        ref="footerRef"
        class="site-footer"
        :class="{
            'site-footer--active': revealActive,
            'site-footer--stop-nudge': stopNudgeActive,
            'site-footer--body-locked': bodyScrollLocked,
        }"
        aria-labelledby="site-footer-title"
        @pointermove="handlePointerMove"
        @pointerleave="resetPointer"
    >
        <div class="site-footer__cursor-light" aria-hidden="true"></div>
        <div class="site-footer__visual" aria-hidden="true">
            <svg viewBox="0 0 720 260" preserveAspectRatio="none" focusable="false">
                <path
                    class="site-footer__wave"
                    d="M18 180C116 87 214 87 312 180C410 273 508 273 702 74"
                    pathLength="1"
                />
                <path
                    class="site-footer__wave site-footer__wave--secondary"
                    d="M48 78C128 146 222 154 330 100C438 46 540 50 672 138"
                    pathLength="1"
                />
                <circle cx="110" cy="104" r="5" />
                <circle cx="312" cy="180" r="5" />
                <circle cx="504" cy="180" r="5" />
                <circle cx="626" cy="108" r="5" />
            </svg>
        </div>

        <div class="site-footer__inner">
            <div class="site-footer__top">
                <section class="site-footer__brand" aria-labelledby="site-footer-title">
                    <RouterLink class="site-footer__logo-link" :to="brandLink" aria-label="Logora">
                        <SvgLogo :width="164" title="Logora" />
                    </RouterLink>
                    <p id="site-footer-title" class="site-footer__title">
                        Платформа для личных сессий, наставничества и экспертных созвонов.
                    </p>
                </section>

                <aside class="site-footer__aside" aria-label="Ключевые элементы доверия">
                    <p class="site-footer__aside-label">Сервис</p>
                    <ul class="site-footer__trust">
                        <li v-for="item in trustItems" :key="item">{{ item }}</li>
                    </ul>
                </aside>
            </div>

            <div class="site-footer__nav-grid">
                <nav
                    v-for="group in navigationGroups"
                    :key="group.title"
                    class="site-footer__nav"
                    :aria-label="group.ariaLabel"
                >
                    <details class="site-footer__nav-details" open>
                        <summary class="site-footer__nav-title">{{ group.title }}</summary>
                        <ul class="site-footer__nav-list">
                            <li v-for="link in group.links" :key="link.label">
                                <RouterLink
                                    v-if="link.to"
                                    class="site-footer__link"
                                    :class="{ 'site-footer__link--cta': link.emphasis }"
                                    :to="link.to"
                                >
                                    {{ link.label }}
                                </RouterLink>
                                <a
                                    v-else
                                    class="site-footer__link"
                                    :class="{ 'site-footer__link--cta': link.emphasis }"
                                    :href="link.href"
                                    :target="link.external ? '_blank' : undefined"
                                    :rel="link.external ? 'noopener noreferrer' : undefined"
                                >
                                    {{ link.label }}
                                </a>
                            </li>
                        </ul>
                    </details>
                </nav>
            </div>

            <section class="site-footer__contact" aria-label="Контакты и реквизиты">
                <div>
                    <h3 class="site-footer__contact-title">Поддержка и документы</h3>
                    <p class="site-footer__contact-text">
                        По вопросам сервиса, платежей, возвратов и обработки персональных данных пишите в поддержку.
                    </p>
                </div>

                <div class="site-footer__contact-links">
                    <a class="site-footer__contact-link" href="mailto:support@logora.ru">
                        support@logora.ru
                    </a>
                    <a class="site-footer__contact-link" href="mailto:office@logora.ru">
                        office@logora.ru
                    </a>
                </div>
            </section>

            <div class="site-footer__bottom">
                <div class="site-footer__company">
                    <span>© {{ currentYear }} Logora</span>
                    <span>ООО «ЛОГОРА», ОГРН 1267700087445, ИНН 9718294498</span>
                    <span>107564, г. Москва, ул. Миллионная, д. 3, к. 1, кв. 88</span>
                </div>

                <button class="site-footer__to-top" type="button" @click="scrollToTop">
                    <span>Наверх</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                        <path d="M8 3.5L3.75 7.75M8 3.5L12.25 7.75M8 3.5V13" />
                    </svg>
                </button>
            </div>
        </div>
    </footer>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import SvgLogo from '../common/SvgLogo.vue';

type FooterLink = {
    label: string;
    to?: string;
    href?: string;
    external?: boolean;
    // Акцентный пункт списка: рисуется как CTA, а не как обычная ссылка.
    emphasis?: boolean;
};

type FooterGroup = {
    title: string;
    ariaLabel: string;
    links: FooterLink[];
};

// Каталог наставников (/AllMentors) временно доступен только админам, поэтому
// футер-ссылки на него (лого-линк и пункт «Все наставники») гейтятся признаком
// isAdmin. Значение прокидывает App.vue (единый источник — useIsAdmin), футер
// остаётся презентационным. Дефолт false ⇒ по умолчанию каталог скрыт.
const props = withDefaults(defineProps<{ isAdmin?: boolean }>(), { isAdmin: false });

const supportUserEnabled =
    String((globalThis as { process?: { env?: Record<string, string | undefined> } })
        .process?.env?.VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED ?? '').toLowerCase() === 'true';

// Лого в футере вело в каталог наставников. Для не-админа каталог скрыт →
// уводим на главную (аутентифицированного корень сам перебросит на /dashboard).
const brandLink = computed<string>(() => (props.isAdmin ? '/AllMentors' : '/'));

const currentYear = computed(() => new Date().getFullYear());
const route = useRoute();
const footerRef = ref<HTMLElement | null>(null);
const footerHeight = ref(720);
const revealActive = ref(false);
const revealProgress = ref(0);
const stopNudgeActive = ref(false);
const bodyScrollLocked = ref(false);
let resizeObserver: ResizeObserver | null = null;
let bodyClassObserver: MutationObserver | null = null;
let animationFrame = 0;
let lastTouchY = 0;
let footerRevealArmed = false;
let footerRevealReadyAt = 0;
let lastWheelDeltaAbs = 0;
let lastWheelEventAt = 0;
let stopNudgeFrame = 0;
let stopNudgeTimeout = 0;
let touchRevealBlockedUntilRelease = false;

const REVEAL_SCROLL_DAMPING = 0.82;
const REVEAL_TRIGGER_TOLERANCE_PX = 2;
const DEFAULT_FOOTER_PEEK_HEIGHT = 28;
const WHEEL_REVEAL_QUIET_GAP_MS = 220;
const WHEEL_REVEAL_LOCK_MS = 160;
const WHEEL_REVEAL_MIN_DELTA = 18;
const WHEEL_REVEAL_DELTA_ACCELERATION_PX = 8;
const WHEEL_REVEAL_DELTA_ACCELERATION_RATIO = 1.28;
const KEY_TOUCH_REVEAL_LOCK_MS = 260;
const STOP_NUDGE_DURATION_MS = 460;
const WHEEL_EVENT_OPTIONS: AddEventListenerOptions = { passive: false, capture: true };
const BODY_SCROLL_LOCK_CLASS = 'body-scroll-locked';
const MODAL_REVEAL_BLOCK_SELECTOR = [
    '[aria-modal="true"]',
    '.v-overlay--active',
    '.v-overlay__content',
    '.modal-overlay',
    '.aw-editor__overlay',
].join(',');

const trustItems = [
    'Безопасная сделка',
    'Поддержка обращений',
    'Публичные правила',
];

const navigationGroups = computed<FooterGroup[]>(() => {
    const supportLinks: FooterLink[] = [
        ...(supportUserEnabled ? [{ label: 'Мои обращения', to: '/support' }] : []),
        { label: 'Написать в поддержку', href: 'mailto:support@logora.ru', emphasis: true },
        { label: 'Вопросы по персональным данным', href: 'mailto:office@logora.ru' },
        { label: 'Дмитрий в Telegram', href: 'https://t.me/logora_dmitry', external: true },
        { label: 'Максим в Telegram', href: 'https://t.me/logora_maxim', external: true },
    ];

    return [
        {
            title: 'Платформа',
            ariaLabel: 'Навигация по платформе',
            links: [
                // Оба каталога (/AllMentors и /AllEvents) на запуске закрыты
                // admin-гейтом на роутах — ссылки показываем только админам.
                ...(props.isAdmin
                    ? [
                        { label: 'Все наставники', to: '/AllMentors' },
                        { label: 'Сессии', to: '/AllEvents' },
                    ]
                    : []),
                { label: 'Войти', to: '/login' },
                { label: 'Регистрация', to: '/register' },
            ],
        },
        {
            title: 'Наставникам',
            ariaLabel: 'Разделы для наставников',
            links: [
                { label: 'Стать наставником', to: '/become-mentor' },
                { label: 'Планирование событий', to: '/CreateEntityPage' },
                { label: 'Календарь', to: '/CalendarPage' },
            ],
        },
        {
            title: 'Поддержка',
            ariaLabel: 'Поддержка пользователей',
            links: supportLinks,
        },
        {
            title: 'Документы',
            ariaLabel: 'Юридические документы',
            links: [
                { label: 'Политика обработки персональных данных', to: '/legal/privacy-policy' },
                { label: 'Публичная оферта', to: '/legal/public-offer' },
                { label: 'Оферта безопасной сделки', to: '/legal/secure-deal-offer' },
            ],
        },
    ];
});

const scrollToTop = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    closeFooterReveal();
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
};

const getMeasuredFooterHeight = () => {
    return footerRef.value?.offsetHeight || window.innerHeight || footerHeight.value;
};

const syncFooterHeight = () => {
    footerHeight.value = getMeasuredFooterHeight();
    updateFooterReveal();
};

const updateFooterReveal = () => {
    if (!footerRef.value) return;

    const footer = footerRef.value;
    const height = getMeasuredFooterHeight();
    const progress = revealProgress.value;
    const hiddenOffset = Math.max(0, height - getFooterPeekHeight());
    const offset = Math.round((1 - progress) * hiddenOffset);

    footerHeight.value = height;
    revealActive.value = progress > 0.01;
    footer.style.setProperty('--site-footer-offset', `${offset}px`);
};

const scheduleFooterRevealUpdate = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        updateFooterReveal();
    });
};

const getTriggerScrollY = () => {
    const scrollElement = document.scrollingElement || document.documentElement;
    return Math.max(0, scrollElement.scrollHeight - window.innerHeight);
};

const getCurrentScrollY = () => {
    const scrollElement = document.scrollingElement || document.documentElement;
    return scrollElement.scrollTop || window.scrollY || window.pageYOffset || 0;
};

const clampRevealProgress = (value: number) => Math.min(1, Math.max(0, value));

const clearFooterStopNudge = () => {
    if (stopNudgeFrame) {
        window.cancelAnimationFrame(stopNudgeFrame);
        stopNudgeFrame = 0;
    }
    if (stopNudgeTimeout) {
        window.clearTimeout(stopNudgeTimeout);
        stopNudgeTimeout = 0;
    }
    stopNudgeActive.value = false;
};

const triggerFooterStopNudge = () => {
    clearFooterStopNudge();
    stopNudgeFrame = window.requestAnimationFrame(() => {
        stopNudgeFrame = 0;
        stopNudgeActive.value = true;
        stopNudgeTimeout = window.setTimeout(() => {
            stopNudgeTimeout = 0;
            stopNudgeActive.value = false;
        }, STOP_NUDGE_DURATION_MS);
    });
};

const resetRevealStop = () => {
    footerRevealArmed = false;
    footerRevealReadyAt = 0;
    lastWheelDeltaAbs = 0;
    lastWheelEventAt = 0;
    touchRevealBlockedUntilRelease = false;
    clearFooterStopNudge();
};

const resetFooterScroll = () => {
    if (footerRef.value?.scrollTop) {
        footerRef.value.scrollTop = 0;
    }
};

const setRevealProgress = (value: number, immediate = false) => {
    revealProgress.value = clampRevealProgress(value);
    if (revealProgress.value > 0) {
        clearFooterStopNudge();
    }
    if (revealProgress.value < 0.99) {
        resetFooterScroll();
    }
    if (revealProgress.value <= 0) {
        resetRevealStop();
    }

    if (immediate) {
        updateFooterReveal();
        return;
    }

    scheduleFooterRevealUpdate();
};

const closeFooterReveal = () => {
    if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
    }
    resetRevealStop();
    revealProgress.value = 0;
    resetFooterScroll();
    updateFooterReveal();
};

const isBodyScrollLocked = () => {
    return typeof document !== 'undefined'
        && document.body.classList.contains(BODY_SCROLL_LOCK_CLASS);
};

const syncBodyScrollLocked = () => {
    const locked = isBodyScrollLocked();
    bodyScrollLocked.value = locked;
    if (locked) {
        closeFooterReveal();
    }
};

const getEventTargetElement = (target: EventTarget | null) => {
    if (target instanceof Element) return target;
    if (target instanceof Node) return target.parentElement;
    return null;
};

const isModalEventTarget = (target: EventTarget | null) => {
    const element = getEventTargetElement(target);
    return Boolean(element?.closest(MODAL_REVEAL_BLOCK_SELECTOR));
};

const shouldIgnoreFooterRevealEvent = (target: EventTarget | null) => {
    if (!isBodyScrollLocked() && !isModalEventTarget(target)) return false;
    resetRevealStop();
    return true;
};

const isAtRevealTrigger = () => {
    return getCurrentScrollY() >= getTriggerScrollY() - REVEAL_TRIGGER_TOLERANCE_PX;
};

const wouldReachRevealTrigger = (deltaY: number) => {
    if (deltaY <= 0) return false;
    return getCurrentScrollY() + deltaY >= getTriggerScrollY() - REVEAL_TRIGGER_TOLERANCE_PX;
};

const scrollToRevealTrigger = () => {
    const triggerScrollY = getTriggerScrollY();
    if (Math.abs(getCurrentScrollY() - triggerScrollY) <= REVEAL_TRIGGER_TOLERANCE_PX) return;

    window.scrollTo({ top: triggerScrollY, behavior: 'auto' });
};

const shouldStopBeforeReveal = (deltaY: number) => {
    return deltaY > 0 && (isAtRevealTrigger() || wouldReachRevealTrigger(deltaY));
};

const getFooterPeekHeight = () => {
    const raw = window.getComputedStyle(document.documentElement)
        .getPropertyValue('--site-footer-peek-height')
        .trim();
    const parsed = Number.parseFloat(raw);

    return Number.isFinite(parsed) ? parsed : DEFAULT_FOOTER_PEEK_HEIGHT;
};

const getSmoothedRevealDelta = (deltaY: number) => {
    const height = footerHeight.value || window.innerHeight || 720;
    const maxDelta = Math.min(220, Math.max(140, height * 0.28));

    return Math.sign(deltaY) * Math.min(Math.abs(deltaY), maxDelta) * REVEAL_SCROLL_DAMPING;
};

const armFooterRevealStop = () => {
    scrollToRevealTrigger();
    footerRevealArmed = true;
    footerRevealReadyAt = window.performance.now() + KEY_TOUCH_REVEAL_LOCK_MS;
    triggerFooterStopNudge();
};

const armWheelFooterRevealStop = (deltaY: number) => {
    const now = window.performance.now();
    armFooterRevealStop();
    footerRevealReadyAt = now + WHEEL_REVEAL_LOCK_MS;
    lastWheelDeltaAbs = Math.abs(deltaY);
    lastWheelEventAt = now;
};

const isFooterRevealReady = () => {
    return footerRevealArmed && window.performance.now() >= footerRevealReadyAt;
};

const prepareFooterReveal = () => {
    scrollToRevealTrigger();
    if (isFooterRevealReady()) return true;

    if (!footerRevealArmed) {
        armFooterRevealStop();
    }

    return false;
};

const isNewWheelPush = (deltaAbs: number, now: number) => {
    if (now < footerRevealReadyAt) return false;
    if (lastWheelEventAt > 0 && now - lastWheelEventAt >= WHEEL_REVEAL_QUIET_GAP_MS) return true;
    if (deltaAbs < WHEEL_REVEAL_MIN_DELTA) return false;

    const accelerationThreshold = Math.max(
        lastWheelDeltaAbs + WHEEL_REVEAL_DELTA_ACCELERATION_PX,
        lastWheelDeltaAbs * WHEEL_REVEAL_DELTA_ACCELERATION_RATIO,
    );

    return deltaAbs >= accelerationThreshold;
};

const prepareWheelFooterReveal = (deltaY: number) => {
    scrollToRevealTrigger();
    const now = window.performance.now();
    const deltaAbs = Math.abs(deltaY);

    if (!footerRevealArmed) {
        armWheelFooterRevealStop(deltaY);
        return false;
    }

    const shouldReveal = isNewWheelPush(deltaAbs, now);
    lastWheelDeltaAbs = deltaAbs;
    lastWheelEventAt = now;

    return shouldReveal;
};

const prepareTouchFooterReveal = () => {
    scrollToRevealTrigger();
    if (isFooterRevealReady() && !touchRevealBlockedUntilRelease) return true;

    if (!footerRevealArmed) {
        armFooterRevealStop();
        touchRevealBlockedUntilRelease = true;
    }

    return false;
};

const isFooterEventTarget = (target: EventTarget | null) => {
    return target instanceof Node && Boolean(footerRef.value?.contains(target));
};

const canFooterConsumeScroll = (deltaY: number, target: EventTarget | null) => {
    const footer = footerRef.value;
    if (!footer || revealProgress.value < 0.99 || !isFooterEventTarget(target)) return false;

    const maxScrollTop = footer.scrollHeight - footer.clientHeight;
    if (maxScrollTop <= 1) return false;

    if (deltaY > 0) return footer.scrollTop < maxScrollTop - 1;
    if (deltaY < 0) return footer.scrollTop > 1;
    return false;
};

const scrollFooterByTouchDelta = (deltaY: number, target: EventTarget | null) => {
    const footer = footerRef.value;
    if (!footer || revealProgress.value < 0.99 || !isFooterEventTarget(target)) return false;

    const maxScrollTop = footer.scrollHeight - footer.clientHeight;
    if (maxScrollTop <= 1 || deltaY === 0) return false;

    const nextScrollTop = Math.min(maxScrollTop, Math.max(0, footer.scrollTop + deltaY));
    if (Math.abs(nextScrollTop - footer.scrollTop) < 0.5) return false;

    footer.scrollTop = nextScrollTop;
    return true;
};

const isInteractiveKeyTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest('a, button, input, textarea, select, summary, [contenteditable="true"]'));
};

const handleRevealDelta = (deltaY: number) => {
    const height = footerHeight.value || window.innerHeight || 1;
    const nextProgress = clampRevealProgress(revealProgress.value + deltaY / height);
    setRevealProgress(nextProgress);
};

const handleWheelReveal = (event: WheelEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && event.deltaY < 0) {
        resetRevealStop();
        return;
    }

    if (!isRevealing && !shouldStopBeforeReveal(event.deltaY)) return;
    if (canFooterConsumeScroll(event.deltaY, event.target)) return;

    event.preventDefault();

    if (!isRevealing && !prepareWheelFooterReveal(event.deltaY)) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(event.deltaY);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handleTouchStartReveal = (event: TouchEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) {
        lastTouchY = 0;
        return;
    }

    lastTouchY = event.touches[0]?.clientY ?? 0;
    touchRevealBlockedUntilRelease = false;
    if (!isAtRevealTrigger() && revealProgress.value <= 0) {
        resetRevealStop();
    }
};

const handleTouchEndReveal = () => {
    touchRevealBlockedUntilRelease = false;
};

const handleTouchMoveReveal = (event: TouchEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const currentY = event.touches[0]?.clientY ?? lastTouchY;
    const deltaY = lastTouchY - currentY;
    lastTouchY = currentY;

    // Когда футер полностью раскрыт и касание внутри — не перехватываем,
    // чтобы iOS мог применить нативную инерцию при скролле
    if (revealProgress.value >= 0.99 && isFooterEventTarget(event.target)) {
        return;
    }

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && deltaY < 0) {
        resetRevealStop();
        return;
    }

    if (scrollFooterByTouchDelta(deltaY, event.target)) {
        event.preventDefault();
        return;
    }

    if (!isRevealing && !shouldStopBeforeReveal(deltaY)) return;
    if (canFooterConsumeScroll(deltaY, event.target)) return;

    event.preventDefault();

    if (!isRevealing && !prepareTouchFooterReveal()) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(deltaY);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handleKeyReveal = (event: KeyboardEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const keyDeltas: Record<string, number> = {
        ArrowDown: 56,
        PageDown: window.innerHeight * 0.72,
        Space: window.innerHeight * 0.72,
        ArrowUp: -56,
        PageUp: window.innerHeight * -0.72,
    };
    const delta = keyDeltas[event.code] ?? keyDeltas[event.key];
    if (!delta) return;
    if ((event.code === 'Space' || event.key === ' ') && isInteractiveKeyTarget(event.target)) return;

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && delta < 0) {
        resetRevealStop();
        return;
    }
    if (!isRevealing && !shouldStopBeforeReveal(delta)) return;
    if (canFooterConsumeScroll(delta, document.activeElement)) return;

    event.preventDefault();

    if (!isRevealing && !prepareFooterReveal()) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(delta);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handlePointerMove = (event: PointerEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    target.style.setProperty('--footer-cursor-x', `${event.clientX - rect.left}px`);
    target.style.setProperty('--footer-cursor-y', `${event.clientY - rect.top}px`);
    target.style.setProperty('--footer-cursor-opacity', '1');
};

const resetPointer = (event: PointerEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    target?.style.setProperty('--footer-cursor-opacity', '0');
};

onMounted(async () => {
    await nextTick();
    syncFooterHeight();
    syncBodyScrollLocked();

    resizeObserver = new ResizeObserver(syncFooterHeight);
    if (footerRef.value) {
        resizeObserver.observe(footerRef.value);
    }
    bodyClassObserver = new MutationObserver(syncBodyScrollLocked);
    bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('wheel', handleWheelReveal, WHEEL_EVENT_OPTIONS);
    window.addEventListener('touchstart', handleTouchStartReveal, { passive: true });
    window.addEventListener('touchmove', handleTouchMoveReveal, { passive: false });
    window.addEventListener('touchend', handleTouchEndReveal);
    window.addEventListener('touchcancel', handleTouchEndReveal);
    window.addEventListener('keydown', handleKeyReveal);
    window.addEventListener('resize', syncFooterHeight);
});

watch(
    () => route.fullPath,
    () => {
        closeFooterReveal();
    },
);

onBeforeUnmount(() => {
    if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
    }
    resizeObserver?.disconnect();
    bodyClassObserver?.disconnect();
    document.removeEventListener('wheel', handleWheelReveal, WHEEL_EVENT_OPTIONS);
    window.removeEventListener('touchstart', handleTouchStartReveal);
    window.removeEventListener('touchmove', handleTouchMoveReveal);
    window.removeEventListener('touchend', handleTouchEndReveal);
    window.removeEventListener('touchcancel', handleTouchEndReveal);
    window.removeEventListener('keydown', handleKeyReveal);
    window.removeEventListener('resize', syncFooterHeight);
});
</script>

<style scoped>
.site-footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100vh;
    height: 100svh;
    max-height: 100vh;
    max-height: 100svh;
    box-sizing: border-box;
    z-index: 80;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    --footer-cursor-x: 50%;
    --footer-cursor-y: 0;
    --footer-cursor-opacity: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent 32%),
        #8162e9;
    border-radius: 24px 24px 0 0;
    box-shadow:
        0 -26px 70px rgba(37, 31, 86, 0.2),
        0 -1px 0 rgba(255, 255, 255, 0.16);
    color: #ffffff;
    pointer-events: none;
    transform: translate3d(0, var(--site-footer-offset, calc(100% - var(--site-footer-peek-height, 28px))), 0);
    will-change: transform;
}

.site-footer--active {
    z-index: 1200;
    pointer-events: auto;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
}

.site-footer--stop-nudge {
    z-index: 90;
    animation: site-footer-stop-nudge 460ms cubic-bezier(0.2, 0.85, 0.25, 1) both;
}

.site-footer--body-locked {
    z-index: 70;
    pointer-events: none;
}

@keyframes site-footer-stop-nudge {
    0% {
        transform: translate3d(0, var(--site-footer-offset, calc(100% - var(--site-footer-peek-height, 28px))), 0);
    }

    42% {
        transform: translate3d(0, calc(var(--site-footer-offset, 100%) - 18px), 0);
    }

    100% {
        transform: translate3d(0, var(--site-footer-offset, calc(100% - var(--site-footer-peek-height, 28px))), 0);
    }
}

.site-footer::before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    z-index: 0;
    height: 72px;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.16), transparent);
}

.site-footer__cursor-light {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: radial-gradient(
        440px circle at var(--footer-cursor-x) var(--footer-cursor-y),
        rgba(255, 255, 255, 0.16),
        rgba(255, 255, 255, 0.06) 34%,
        transparent 64%
    );
    opacity: var(--footer-cursor-opacity);
    transition: opacity 180ms ease;
}

.site-footer__visual {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.28;
}

.site-footer__visual svg {
    position: absolute;
    right: -6%;
    top: -10px;
    width: min(680px, 58vw);
    height: 240px;
}

.site-footer__visual path {
    fill: none;
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 2;
    stroke-linecap: round;
}

.site-footer__visual circle {
    fill: rgba(255, 255, 255, 0.34);
}

.site-footer__wave {
    stroke-dasharray: 1;
    stroke-dashoffset: 0;
    transition: stroke-dashoffset 700ms ease;
}

.site-footer:hover .site-footer__wave {
    stroke-dashoffset: -0.08;
}

.site-footer__wave--secondary {
    stroke: rgba(192, 255, 236, 0.2);
}

.site-footer__inner {
    position: relative;
    z-index: 1;
    max-width: 1240px;
    margin: 0 auto;
    min-height: 100%;
    padding: clamp(36px, 6vh, 52px) 32px clamp(40px, 7vh, 56px);
}

.site-footer__top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 40px;
    align-items: end;
}

.site-footer__brand {
    max-width: 700px;
}

.site-footer__logo-link {
    display: inline-flex;
    align-items: center;
    margin-bottom: 16px;
    border-radius: 12px;
}

.site-footer__logo-link :deep(path) {
    fill: #ffffff;
}

.site-footer__logo-link:focus-visible,
.site-footer__link:focus-visible,
.site-footer__contact-link:focus-visible,
.site-footer__to-top:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.46);
    outline-offset: 4px;
}

.site-footer__title {
    margin: 0;
    max-width: 520px;
    color: #ffffff;
    font-size: 17px;
    font-weight: 560;
    line-height: 1.45;
    letter-spacing: 0;
}

.site-footer__aside {
    justify-self: end;
    width: min(100%, 430px);
}

.site-footer__aside-label {
    margin: 0 0 10px;
    color: rgba(255, 255, 255, 0.66);
    font-size: 12px;
    font-weight: 780;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.site-footer__trust {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.site-footer__trust li {
    display: inline-flex;
    min-height: 30px;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 999px;
    padding: 0 10px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.88);
    font-size: 13px;
    line-height: 1;
}

.site-footer__nav-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 28px;
    margin-top: clamp(32px, 5vh, 44px);
    padding-top: 28px;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.site-footer__nav-details {
    display: block;
}

.site-footer__nav-title {
    list-style: none;
    color: #ffffff;
    font-size: 14px;
    font-weight: 780;
    cursor: default;
}

.site-footer__nav-title::-webkit-details-marker {
    display: none;
}

.site-footer__nav-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
}

.site-footer__link {
    position: relative;
    display: inline-flex;
    color: rgba(255, 255, 255, 0.76);
    font-size: 15px;
    line-height: 1.35;
    text-decoration: none;
    transition: color 160ms ease, transform 160ms ease;
}

.site-footer__link::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -3px;
    height: 1px;
    background: currentColor;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 160ms ease;
}

.site-footer__link:hover {
    color: #ffffff;
}

.site-footer__link:hover::after {
    transform: scaleX(1);
}

/* SCHED-05: «Написать в поддержку» — единственный выход в поддержку из подвала,
   и среди пяти одинаковых пунктов группы её не находили. Подчёркивание держим
   постоянным, а не на :hover, — на тач-устройствах наведения не бывает. */
.site-footer__link--cta {
    color: #ffffff;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
}

/* Hover-линия ::after дала бы вторую полосу под уже подчёркнутым текстом. */
.site-footer__link--cta::after {
    content: none;
}

.site-footer__contact {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 24px;
    align-items: center;
    margin-top: clamp(32px, 5vh, 44px);
    border-top: 1px solid rgba(255, 255, 255, 0.18);
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    padding: 24px 0;
}

.site-footer__contact-title {
    margin: 0;
    color: #ffffff;
    font-size: 18px;
    font-weight: 780;
}

.site-footer__contact-text {
    max-width: 660px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 14px;
    line-height: 1.5;
}

.site-footer__contact-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
}

.site-footer__contact-link {
    display: inline-flex;
    min-height: 40px;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 8px;
    padding: 0 12px;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    font-size: 14px;
    font-weight: 740;
    text-decoration: none;
    transition: background-color 160ms ease, border-color 160ms ease;
}

.site-footer__contact-link:hover {
    border-color: rgba(255, 255, 255, 0.46);
    background: rgba(255, 255, 255, 0.16);
}

.site-footer__bottom {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    align-items: flex-start;
    margin-top: 24px;
}

.site-footer__company {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 18px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 13px;
    line-height: 1.45;
}

.site-footer__to-top {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    border: 0;
    border-radius: 8px;
    padding: 8px 0;
    background: transparent;
    color: #ffffff;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 740;
    transition: color 160ms ease, transform 160ms ease;
}

.site-footer__to-top svg {
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 160ms ease;
}

.site-footer__to-top:hover {
    color: #e9e3ff;
}

.site-footer__to-top:hover svg {
    transform: translateY(-2px);
}

@media (max-width: 1024px) {
    .site-footer__top {
        grid-template-columns: 1fr;
        gap: 24px;
        align-items: start;
    }

    .site-footer__aside {
        justify-self: stretch;
        width: 100%;
        border-top: 1px solid rgba(255, 255, 255, 0.16);
        padding-top: 20px;
    }

    .site-footer__nav-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .site-footer__visual svg {
        width: 820px;
        opacity: 0.42;
    }
}

@media (max-width: 720px) {
    .site-footer {
        border-radius: 20px 20px 0 0;
    }

    .site-footer__inner {
        padding: 34px 20px calc(env(safe-area-inset-bottom) + 40px);
    }

    .site-footer__title {
        font-size: 15px;
    }

    .site-footer__trust {
        grid-template-columns: 1fr;
    }

    .site-footer__nav-grid {
        grid-template-columns: 1fr;
        gap: 0;
        margin-top: 40px;
        border-top: 1px solid rgba(255, 255, 255, 0.18);
    }

    .site-footer__nav {
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }

    .site-footer__nav-details {
        padding: 18px 0;
    }

    .site-footer__nav-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
    }

    .site-footer__nav-title::after {
        content: "+";
        color: rgba(255, 255, 255, 0.82);
        font-size: 20px;
        font-weight: 520;
        line-height: 1;
    }

    .site-footer__nav-details[open] .site-footer__nav-title::after {
        content: "-";
    }

    .site-footer__contact {
        grid-template-columns: 1fr;
        margin-top: 36px;
    }

    .site-footer__contact-links {
        justify-content: flex-start;
    }

    .site-footer__bottom {
        flex-direction: column;
    }
}

@media (prefers-reduced-motion: reduce) {
    .site-footer *,
    .site-footer *::after {
        transition-duration: 0.01ms !important;
    }

    .site-footer:hover .site-footer__wave {
        stroke-dashoffset: 0;
    }

    .site-footer__cursor-light {
        display: none;
    }
}
</style>
